import { useProjectStore } from "@/frontend/store/ProjectStore";
import { I18n } from "@/i18n/I18n";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Breadcrumbs } from "../Breadcrumbs";
import { useShallow } from "zustand/react/shallow";
import { Dropdown, Placeholder, Button } from "react-bootstrap";
import { ConfirmModal } from "../modals/ConfirmModal";
import { CustomToggle } from "../dropdown/CustomToggle";
import { useWorkspaceStore } from "@/frontend/store/WorkspaceStore";
import { useFusaOrganizationStore } from "@/frontend/store/FusaOrganizationStore";
import { PartType } from "@/backend/access/part/types";
import { OffcanvasWrapper } from "../OffcanvasWrapper";
import { ParameterTable } from "../parameters/ParameterTable";
import { ParameterType, ParameterSubjectType } from "@/types/parameterTypes";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { ParameterItemRun } from "../parameters/ParameterItemRun";
import { useProjectLockStore } from "@/frontend/store/ProjectLockStore";
import { ProjectLocked } from "../projects/ProjectLocked";
import { NotFound } from "@/frontend/pages/NotFound";

function PartViewPlaceholder() {
  return (
    <div className="p-4" aria-label={I18n.get('loading')}>
      <nav aria-label="breadcrumb">
        <div className="d-flex gap-2">
          <div className="d-flex justify-content-between w-100">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Placeholder animation="wave">
                  <Placeholder xs={4} bg="secondary" />
                </Placeholder>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                <Placeholder animation="wave">
                  <Placeholder xs={6} bg="secondary" />
                </Placeholder>
              </li>
            </ol>
          </div>
        </div>
      </nav>
      <div className="card">
        <div className="card-header">
          <Placeholder as="h3" animation="wave" className="card-title mb-0">
            <Placeholder xs={8} bg="secondary" />
          </Placeholder>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column gap-3">
            <Placeholder animation="wave">
              <Placeholder xs={12} bg="secondary" />
              <Placeholder xs={10} bg="secondary" />
              <Placeholder xs={7} bg="secondary" />
            </Placeholder>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PartView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const projectPart = useProjectStore(useShallow(state => {
    return state.projectParts.find(p => p.id === id) || null;
  }));
  const readPart = useProjectStore(state => state.readPart);
  const deletePart = useProjectStore(state => state.deletePart);
  const getParametersForSubject = useParameterStore(state => state.getParametersForSubject);
  
  const fetchProject = useProjectStore(state => state.fetchProject);
  const project = useProjectStore(useShallow(state => 
    projectPart ? state.projects.find(project => project.id === projectPart.projectId) : null
  ));
  const projectLock = useProjectLockStore(useShallow(state => state.projectLocks.find(p => p.projectId === projectPart?.projectId)));
  const workspace = useWorkspaceStore(useShallow(state => state.workspaces.find(workspace => workspace.id === project?.parentId)));
  const fusaOrganization = useFusaOrganizationStore(useShallow(state => state.fusaOrganizations.find(fusaOrganization => fusaOrganization.id === workspace?.ownerFusaOrgId)));
  
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showParametersOffcanvas, setShowParametersOffcanvas] = useState(false);

  const resetProjectStore = useProjectStore(state => state.reset);
  const resetParameterStore = useParameterStore(state => state.reset);

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoading(true);
    void readPart(id).finally(() => {
      setLoading(false);
    });

    return () => {
      resetProjectStore();
    };
  }, [readPart, fetchProject]);

  useEffect(() => {
    if (!project && projectPart?.projectId) {
      void fetchProject(projectPart.projectId);
    }
  }, [projectPart?.projectId, fetchProject]);

  useEffect(() => {
    if (projectPart?.id) {
      void getParametersForSubject(projectPart.id, ParameterSubjectType.PART);
    }

    return () => {
      resetParameterStore();
    };
  }, [getParametersForSubject, projectPart]);

  const handleDelete = async () => {
    if (!id || !projectPart) return;
    await deletePart(projectPart.projectId, id);
    const tab = projectPart.type === PartType.TEMPLATE ? 'template' : 'part_instance';
    void navigate(`/projects/${projectPart.projectId}/parts?tab=${tab}`);
  };

  const part = projectPart;
  const projectId = part?.projectId;
  const parameters = useParameterStore(useShallow(state => 
    part?.id ? state.parametersForSubject(part.id, ParameterSubjectType.PART) : []
  ));

  if (loading) {
    return (
      <div className="d-flex">
        <div className="flex-grow-1">
          <PartViewPlaceholder />
        </div>
      </div>
    );
  }

  if(projectLock?.isLocked()) {
    return <ProjectLocked />;
  }

  if (!projectPart || !projectId) {
    return <NotFound />;
  }

  return (
    <div className="d-flex">
      <div className="flex-grow-1 overflow-auto" style={{ maxHeight: '100vh', height: '100vh' }}>
        <Breadcrumbs items={[
          ...(fusaOrganization ? [{ title: fusaOrganization.title, link: `/fusa-organizations/${fusaOrganization.id}` }] : []),
          ...(workspace ? [{ title: workspace.title, link: `/workspaces/${workspace.id}` }] : []),
          ...(project ? [{ title: project.title, link: `/projects/${project.id}` }] : []),
          { title: part.type === PartType.TEMPLATE ? I18n.get('partTemplates') : I18n.get('parts'), link: `/projects/${part.projectId}/parts?tab=${part.type === PartType.TEMPLATE ? 'template' : 'part_instance'}` },
          { title: part.title },
        ]} />
        <div className="p-4">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h3 className="card-title mb-0">{part.title}</h3>
          <Dropdown drop="down">
            <Dropdown.Toggle size="sm" as={CustomToggle('part')} variant="dark" id={`part-dropdown-${part.id}`} />
            <Dropdown.Menu>
              <Dropdown.Item className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {I18n.get('delete')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column gap-3">
            <div>
              <small className="text-muted text-uppercase">{I18n.get('type')}</small>
              <p className="mb-0">{part.type}</p>
            </div>
            <div>
              <small className="text-muted text-uppercase">{I18n.get('description')}</small>
              <p className="text-body-secondary mb-0">{part.description || I18n.get('noDescription') || 'No description'}</p>
            </div>
            {part.type === PartType.TEMPLATE && (
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowParametersOffcanvas(true)}
                >
                  {I18n.get('manageProperties')}
                </Button>
              </div>
            )}
            {part.createdAt && (
              <div>
                <small className="text-muted text-uppercase">{I18n.get('createdAt')}</small>
                <p className="mb-0">{new Date(part.createdAt).toLocaleString()}</p>
              </div>
            )}
            {part.updatedAt && (
              <div>
                <small className="text-muted text-uppercase">{I18n.get('updatedAt')}</small>
                <p className="mb-0">{new Date(part.updatedAt).toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {parameters.length > 0 && (
        <div className="card mt-3">
          <div className="card-header">
            <h5 className="card-title mb-0">{I18n.get('Properties') || 'Properties'}</h5>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column gap-3">
              {parameters.map((parameter, index) => (
                <div key={parameter.id} className={index < parameters.length - 1 ? "border-bottom pb-3" : ""}>
                  <ParameterItemRun parameter={parameter} alwaysEnabled={false} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmModal
          title={I18n.get('deleteModel', { model: I18n.get('part'), title: part.title })}
          confirmMessage={I18n.get('areYouSureDeleteModel', { model: I18n.get('part'), title: part.title })}
          onConfirm={() => handleDelete()}
          setShowModal={setShowDeleteModal}
        />
      )}

      {part.type === PartType.TEMPLATE && (
        <OffcanvasWrapper
          show={showParametersOffcanvas}
          onHide={() => setShowParametersOffcanvas(false)}
          id="part-parameters-offcanvas"
          label={I18n.get('Properties')}
        >
          <div className="d-flex flex-column gap-3">
            <div>
              <ParameterTable
                subjectId={part.id}
                parameterType={ParameterType.FIELD}
                subjectType={ParameterSubjectType.PART}
              />
            </div>
          </div>
        </OffcanvasWrapper>
      )}
      </div>
      </div>
    </div>
  );
}
